import { useState, useMemo, useCallback } from 'react';
import {
  orderBy,
  where,
  limit,
  Timestamp,
  type QueryConstraint,
} from 'firebase/firestore';
import {
  AlertTriangle,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Clock,
  Filter,
  KeyRound,
  Link2,
  LogOut,
  Mail,
  Monitor,
  Search,
  Shield,
  ShieldAlert,
  ShieldCheck,
  User,
  UserCheck,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { useCollection } from '../../hooks/useFirestore';
import type { RegistroAuditoria, EventoAuditoria, MedicoCompleto, MedicoPreRegistro, Sala } from '../../types';
import styles from './AuditoriaPage.module.css';

// ─── Human-readable event metadata ──────────────────────────

type EventoMeta = {
  label: string;
  descripcion: string;
  icono: typeof Shield;
  tipo: 'usuario' | 'sistema';
  variant: 'success' | 'error' | 'warning' | 'info' | 'neutral';
};

const EVENTO_META: Record<EventoAuditoria, EventoMeta> = {
  REGISTRO_EXITOSO: {
    label: 'Registro completado',
    descripcion: 'Un nuevo usuario completó su registro en el sistema',
    icono: UserCheck,
    tipo: 'sistema',
    variant: 'success',
  },
  REGISTRO_RECHAZADO: {
    label: 'Registro rechazado',
    descripcion: 'Se rechazó un intento de registro por no cumplir requisitos',
    icono: UserMinus,
    tipo: 'sistema',
    variant: 'error',
  },
  LOGIN_EXITOSO: {
    label: 'Inicio de sesión',
    descripcion: 'Un usuario inició sesión correctamente',
    icono: KeyRound,
    tipo: 'sistema',
    variant: 'success',
  },
  LOGIN_FALLIDO: {
    label: 'Inicio de sesión fallido',
    descripcion: 'Un intento de inicio de sesión falló (credenciales incorrectas)',
    icono: ShieldAlert,
    tipo: 'sistema',
    variant: 'error',
  },
  LOGIN_BLOQUEADO: {
    label: 'Cuenta bloqueada',
    descripcion: 'Se bloqueó el acceso por múltiples intentos fallidos',
    icono: ShieldAlert,
    tipo: 'sistema',
    variant: 'error',
  },
  MAGIC_LINK_ENVIADO: {
    label: 'Enlace mágico enviado',
    descripcion: 'Se envió un enlace de acceso por correo electrónico',
    icono: Mail,
    tipo: 'sistema',
    variant: 'info',
  },
  MAGIC_LINK_USADO: {
    label: 'Enlace mágico utilizado',
    descripcion: 'Un usuario accedió al sistema usando un enlace mágico',
    icono: Link2,
    tipo: 'sistema',
    variant: 'success',
  },
  MAGIC_LINK_RECHAZADO: {
    label: 'Enlace mágico rechazado',
    descripcion: 'Se rechazó un enlace mágico (expirado o inválido)',
    icono: Link2,
    tipo: 'sistema',
    variant: 'error',
  },
  RESET_PASSWORD_SOLICITADO: {
    label: 'Restablecimiento solicitado',
    descripcion: 'Se solicitó restablecer la contraseña de una cuenta',
    icono: KeyRound,
    tipo: 'sistema',
    variant: 'info',
  },
  RESET_PASSWORD_COMPLETADO: {
    label: 'Contraseña restablecida',
    descripcion: 'Se completó el restablecimiento de contraseña',
    icono: KeyRound,
    tipo: 'sistema',
    variant: 'success',
  },
  MEDICO_PREREGISTRADO: {
    label: 'Médico pre-registrado',
    descripcion: 'Un administrador pre-registró un nuevo médico en el sistema',
    icono: UserPlus,
    tipo: 'usuario',
    variant: 'warning',
  },
  MEDICO_DESACTIVADO: {
    label: 'Médico desactivado',
    descripcion: 'Un administrador desactivó la cuenta de un médico',
    icono: UserMinus,
    tipo: 'usuario',
    variant: 'error',
  },
  MEDICO_REACTIVADO: {
    label: 'Médico reactivado',
    descripcion: 'Un administrador reactivó la cuenta de un médico',
    icono: UserCheck,
    tipo: 'usuario',
    variant: 'success',
  },
  LOGOUT: {
    label: 'Cierre de sesión',
    descripcion: 'Un usuario cerró su sesión manualmente',
    icono: LogOut,
    tipo: 'sistema',
    variant: 'neutral',
  },
  SESION_EXPIRADA: {
    label: 'Sesión expirada',
    descripcion: 'Una sesión expiró por inactividad',
    icono: Clock,
    tipo: 'sistema',
    variant: 'neutral',
  },
  SEGUNDO_FACTOR_GLOBAL_DESACTIVADO: {
    label: 'Doble factor desactivado para todos',
    descripcion: 'Se desactivó el doble factor de autenticación para todos los usuarios del portal',
    icono: ShieldAlert,
    tipo: 'usuario',
    variant: 'error',
  },
  SEGUNDO_FACTOR_GLOBAL_ACTIVADO: {
    label: 'Doble factor activado para todos',
    descripcion: 'Se volvió a exigir el doble factor de autenticación a todos los usuarios (salvo los eximidos)',
    icono: Shield,
    tipo: 'usuario',
    variant: 'success',
  },
};

const getMeta = (evento: string): EventoMeta => {
  return (
    EVENTO_META[evento as EventoAuditoria] ?? {
      label: evento,
      descripcion: 'Evento del sistema',
      icono: Shield,
      tipo: 'sistema' as const,
      variant: 'neutral' as const,
    }
  );
};

// ─── Filter categories ──────────────────────────────────────

type FiltroTipo = '' | 'usuario' | 'sistema';

const FILTER_CATEGORIES: Record<string, EventoAuditoria[]> = {
  Acceso: ['LOGIN_EXITOSO', 'LOGIN_FALLIDO', 'LOGIN_BLOQUEADO', 'LOGOUT', 'SESION_EXPIRADA'],
  Registro: ['REGISTRO_EXITOSO', 'REGISTRO_RECHAZADO'],
  'Enlace mágico': ['MAGIC_LINK_ENVIADO', 'MAGIC_LINK_USADO', 'MAGIC_LINK_RECHAZADO'],
  Contraseña: ['RESET_PASSWORD_SOLICITADO', 'RESET_PASSWORD_COMPLETADO'],
  'Gestión médicos': ['MEDICO_PREREGISTRADO', 'MEDICO_DESACTIVADO', 'MEDICO_REACTIVADO'],
  'Doble factor': ['SEGUNDO_FACTOR_GLOBAL_DESACTIVADO', 'SEGUNDO_FACTOR_GLOBAL_ACTIVADO'],
};

// ─── ID resolution helpers ──────────────────────────────────

type ResolverMap = {
  medicos: Map<string, string>;
  salas: Map<string, string>;
};

/** Fields that are internal IDs — hidden from the main view, shown only in technical section */
const HIDDEN_ID_FIELDS = new Set([
  'uid', 'medicoUid', 'medicoId', 'preRegistroId', 'salaId', 'salaOrigenId',
  'salaDestinoId', 'consultaId', 'firebaseUid', 'docId', 'id',
  'usuarioAfectado', 'targetUid', 'otorgadaPor', 'realizadoPor', 'canceladoPor',
  'asignadoPor', 'supervisorId', 'adminUid', 'pacienteId', 'pacienteUid',
  'campanaId', 'registroId',
]);

/** Campos cuyo valor es el identificador de una persona. */
const MEDICO_UID_FIELDS = new Set([
  'uid', 'medicoUid', 'medicoId', 'firebaseUid', 'usuarioAfectado', 'targetUid',
  'otorgadaPor', 'realizadoPor', 'creadoPor', 'canceladoPor', 'asignadoPor',
  'supervisorId', 'adminUid', 'pacienteId', 'pacienteUid',
]);

/** Campos cuyo valor es el identificador de una sala. */
const SALA_ID_FIELDS = new Set(['salaId', 'salaOrigenId', 'salaDestinoId']);

function resolveUid(uid: string, resolvers: ResolverMap): string {
  return resolvers.medicos.get(uid) ?? uid;
}

function resolveSalaId(salaId: string, resolvers: ResolverMap): string {
  return resolvers.salas.get(salaId) ?? salaId;
}

/**
 * Returns the display name for a log entry using a fallback chain:
 * 1. Resolved UID name from medicos collection
 * 2. Name stored in detalles (nombre, adminEmail, creadoPor)
 * 3. Top-level email or detalles.email
 * 4. "Sistema" if no user info at all
 */
function getDisplayUser(log: RegistroAuditoria, resolvers: ResolverMap): string {
  const d = log.detalles as Record<string, unknown> | undefined;

  // El nombre que el backend resolvió al registrar el evento manda: es el que
  // había en ese momento y sobrevive a que la cuenta cambie o se borre.
  const resolvedName = log.actorNombre
    || (log.uid ? resolvers.medicos.get(log.uid) : undefined);

  // Fallback name from detalles fields
  const detailNombre = d?.nombre ? String(d.nombre) : undefined;
  const detailApellidos = d?.apellidos ? String(d.apellidos) : undefined;
  const nameFromDetails = detailNombre
    ? detailApellidos ? `${detailNombre} ${detailApellidos}` : detailNombre
    : undefined;

  // Best available email
  const email = log.email
    || (d?.email ? String(d.email) : undefined)
    || (d?.adminEmail ? String(d.adminEmail) : undefined)
    || (d?.creadoPor ? String(d.creadoPor) : undefined);

  // Best available name
  const name = resolvedName || nameFromDetails;

  if (name && email) return `${name} (${email})`;
  if (name) return name;
  if (email) return email;
  if (log.uid) return 'Usuario del sistema';
  return 'Sistema';
}

// ─── User-friendly detail fields ────────────────────────────

const DETAIL_LABELS: Record<string, string> = {
  email: 'Correo electrónico',
  nombre: 'Nombre',
  apellidos: 'Apellidos',
  especialidad: 'Especialidad',
  colegiadoId: 'No. colegiado',
  centroMedico: 'Centro médico',
  salasAsignadas: 'Salas asignadas',
  metodoAuth: 'Método de autenticación',
  adminEmail: 'Realizado por',
  creadoPor: 'Realizado por',
  intentosFallidos: 'Intentos fallidos',
  razon: 'Razón',
  motivo: 'Motivo',
  rol: 'Rol asignado',
  ip: 'Dirección IP',
  camposCambiados: 'Campos modificados',
  usuarioAfectado: 'Usuario afectado',
  medicoId: 'Médico',
  pacienteId: 'Paciente',
  pacienteUid: 'Paciente',
  salaId: 'Sala',
  salaOrigenId: 'Sala de origen',
  salaDestinoId: 'Sala de destino',
  otorgadaPor: 'Otorgada por',
  realizadoPor: 'Realizado por',
  canceladoPor: 'Cancelado por',
  asignadoPor: 'Asignado por',
  supervisorId: 'Supervisor',
  targetUid: 'Usuario afectado',
  consultaId: 'Consulta',
  campanaId: 'Campaña',
  metodo: 'Método',
  origen: 'Origen',
  teniaFactor: 'Ya tenía segundo factor',
  respaldosRestantes: 'Códigos de respaldo restantes',
  usuariosAfectados: 'Usuarios afectados',
};

function formatDetailValue(key: string, value: unknown, resolvers: ResolverMap): string {
  if (value === null || value === undefined) return '-';

  // Resolve UID fields to names
  if (MEDICO_UID_FIELDS.has(key) && typeof value === 'string') {
    return resolveUid(value, resolvers);
  }
  if (SALA_ID_FIELDS.has(key) && typeof value === 'string') {
    return resolveSalaId(value, resolvers);
  }

  // Resolve sala IDs inside arrays (e.g. salasAsignadas)
  if (key === 'salasAsignadas' && Array.isArray(value)) {
    return value.map((id) => resolvers.salas.get(String(id)) ?? String(id)).join(', ');
  }

  // Translate field names in camposCambiados to human-readable labels
  if (key === 'camposCambiados' && Array.isArray(value)) {
    const CAMPO_LABELS: Record<string, string> = {
      nombre: 'Nombre',
      apellidos: 'Apellidos',
      telefono: 'Teléfono',
      especialidad: 'Especialidad',
      subespecialidad: 'Subespecialidad',
      centroMedico: 'Centro médico',
      role: 'Rol',
      salasAsignadas: 'Salas asignadas',
      skills: 'Skills avanzados',
    };
    return value.map((f) => CAMPO_LABELS[String(f)] ?? String(f)).join(', ');
  }

  if (Array.isArray(value)) {
    if (value.every((v) => v === null || typeof v !== 'object')) return value.join(', ');
    // Array de objetos (ej. `resultados` de cancelarConsultasSupervisor): no
    // usar join (produce "[object Object]"), formatear cada elemento.
    return value.map((v) => (v && typeof v === 'object' ? JSON.stringify(v) : String(v))).join('; ');
  }
  if (typeof value === 'object') return JSON.stringify(value);

  if (key === 'metodoAuth') {
    return value === 'email_password' ? 'Correo y contraseña' : value === 'magic_link' ? 'Enlace mágico' : String(value);
  }
  if (key === 'rol' || key === 'role') {
    const roles: Record<string, string> = { admin: 'Administrador', supervisor: 'Supervisor', medico: 'Médico' };
    return roles[String(value)] ?? String(value);
  }

  return String(value);
}

function formatDetailLabel(key: string): string {
  return DETAIL_LABELS[key] ?? key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, (s) => s.toUpperCase());
}

// ─── Date helpers ───────────────────────────────────────────

function toStartOfDay(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 0, 0, 0);
}

function toEndOfDay(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59);
}

const formatDate = (ts: unknown) => {
  if (!ts) return '-';
  const t = ts as { toDate?: () => Date; seconds?: number };
  const date = t.toDate ? t.toDate() : t.seconds ? new Date(t.seconds * 1000) : null;
  if (!date) return '-';
  return date.toLocaleString('es-CR', { dateStyle: 'short', timeStyle: 'medium' });
};

const formatDateLong = (ts: unknown) => {
  if (!ts) return '-';
  const t = ts as { toDate?: () => Date; seconds?: number };
  const date = t.toDate ? t.toDate() : t.seconds ? new Date(t.seconds * 1000) : null;
  if (!date) return '-';
  return date.toLocaleString('es-CR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const timeAgo = (ts: unknown): string => {
  if (!ts) return '';
  const t = ts as { toDate?: () => Date; seconds?: number };
  const date = t.toDate ? t.toDate() : t.seconds ? new Date(t.seconds * 1000) : null;
  if (!date) return '';
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Hace un momento';
  if (mins < 60) return `Hace ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Hace ${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Hace ${days}d`;
  return '';
};

// ─── Component ──────────────────────────────────────────────

export default function AuditoriaPage() {
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTipo, setFilterTipo] = useState<FiltroTipo>('');
  const [filterFechaDesde, setFilterFechaDesde] = useState('');
  const [filterFechaHasta, setFilterFechaHasta] = useState('');
  const [selectedLog, setSelectedLog] = useState<RegistroAuditoria | null>(null);

  // ─── Fetch lookup data for ID resolution ───────────────────
  const { data: medicosRaw } = useCollection<MedicoCompleto>('medicos', [orderBy('nombreCompleto', 'asc')]);
  const { data: preRegistrosRaw } = useCollection<MedicoPreRegistro>('medicos_preregistro');
  const { data: salasRaw } = useCollection<Sala>('salas');

  const resolvers: ResolverMap = useMemo(() => {
    const medicos = new Map<string, string>();

    // Registered medicos (primary source)
    if (medicosRaw) {
      for (const m of medicosRaw) {
        const name = m.nombreCompleto || `${m.nombre} ${m.apellidos}`;
        medicos.set(m.id, name);
        if (m.email) medicos.set(m.email, name);
      }
    }

    // Pre-registered medicos (fallback for users not yet fully registered)
    if (preRegistrosRaw) {
      for (const p of preRegistrosRaw) {
        const name = `${p.nombre} ${p.apellidos}`;
        // Only add if not already resolved from medicos
        if (p.firebaseUid && !medicos.has(p.firebaseUid)) {
          medicos.set(p.firebaseUid, name);
        }
        if (p.email && !medicos.has(p.email)) {
          medicos.set(p.email, name);
        }
        // Also index by pre-registro doc ID
        if (!medicos.has(p.id)) {
          medicos.set(p.id, name);
        }
      }
    }

    const salas = new Map<string, string>();
    if (salasRaw) {
      for (const s of salasRaw) {
        salas.set(s.id, s.nombre);
      }
    }
    return { medicos, salas };
  }, [medicosRaw, preRegistrosRaw, salasRaw]);

  // ─── Audit log query ──────────────────────────────────────
  const constraints: QueryConstraint[] = useMemo(() => {
    const c: QueryConstraint[] = [orderBy('creadoEn', 'desc'), limit(200)];

    if (filterCategory) {
      const events = FILTER_CATEGORIES[filterCategory];
      if (events && events.length <= 10) {
        c.unshift(where('evento', 'in', events));
      }
    }

    if (filterFechaDesde) {
      c.unshift(where('creadoEn', '>=', Timestamp.fromDate(toStartOfDay(filterFechaDesde))));
    }
    if (filterFechaHasta) {
      c.unshift(where('creadoEn', '<=', Timestamp.fromDate(toEndOfDay(filterFechaHasta))));
    }

    return c;
  }, [filterCategory, filterFechaDesde, filterFechaHasta]);

  const { data: logsRaw, loading } = useCollection<RegistroAuditoria>('auditoria', constraints);
  const logs = useMemo(() => logsRaw || [], [logsRaw]);

  const filtered = useMemo(() => {
    let result = logs;

    if (filterTipo) {
      result = result.filter((l) => getMeta(l.evento).tipo === filterTipo);
    }

    if (search) {
      const q = search.toLowerCase();
      result = result.filter((l) => {
        const displayUser = getDisplayUser(l, resolvers);
        return (
          getMeta(l.evento).label.toLowerCase().includes(q) ||
          displayUser.toLowerCase().includes(q) ||
          l.email?.toLowerCase().includes(q) ||
          JSON.stringify(l.detalles).toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [logs, filterTipo, search, resolvers]);

  // Stats
  const stats = useMemo(() => {
    const cambiosUsuario = logs.filter((l) => getMeta(l.evento).tipo === 'usuario').length;
    const eventosSistema = logs.filter((l) => getMeta(l.evento).tipo === 'sistema').length;
    const alertas = logs.filter((l) => getMeta(l.evento).variant === 'error').length;
    const uniqueUsers = new Set(logs.map((l) => l.email || l.uid).filter(Boolean)).size;
    return { cambiosUsuario, eventosSistema, alertas, uniqueUsers };
  }, [logs]);

  const handleRowClick = useCallback((log: RegistroAuditoria) => {
    setSelectedLog(log);
  }, []);

  return (
    <MainLayout>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>
              <ShieldCheck size={22} /> Auditoría de Seguridad
            </h1>
            <p className={styles.subtitle}>
              Historial de actividad y cambios realizados en el sistema
            </p>
          </div>
        </div>

        {/* Stats cards */}
        <div className={styles.statsRow}>
          <button
            type="button"
            className={`${styles.statCard} ${filterTipo === 'usuario' ? styles.statCardActive : ''}`}
            onClick={() => setFilterTipo(filterTipo === 'usuario' ? '' : 'usuario')}
          >
            <div className={`${styles.statIconWrap} ${styles.statIconUsuario}`}>
              <UserPlus size={18} />
            </div>
            <div>
              <span className={styles.statValue}>{stats.cambiosUsuario}</span>
              <span className={styles.statLabel}>Cambios de usuario</span>
            </div>
          </button>

          <button
            type="button"
            className={`${styles.statCard} ${filterTipo === 'sistema' ? styles.statCardActive : ''}`}
            onClick={() => setFilterTipo(filterTipo === 'sistema' ? '' : 'sistema')}
          >
            <div className={`${styles.statIconWrap} ${styles.statIconSistema}`}>
              <Monitor size={18} />
            </div>
            <div>
              <span className={styles.statValue}>{stats.eventosSistema}</span>
              <span className={styles.statLabel}>Eventos del sistema</span>
            </div>
          </button>

          <div className={styles.statCard}>
            <div className={`${styles.statIconWrap} ${styles.statIconAlerta}`}>
              <AlertTriangle size={18} />
            </div>
            <div>
              <span className={styles.statValue}>{stats.alertas}</span>
              <span className={styles.statLabel}>Alertas de seguridad</span>
            </div>
          </div>

          <div className={styles.statCard}>
            <div className={`${styles.statIconWrap} ${styles.statIconUser}`}>
              <User size={18} />
            </div>
            <div>
              <span className={styles.statValue}>{stats.uniqueUsers}</span>
              <span className={styles.statLabel}>Usuarios involucrados</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.searchBox}>
            <Input
              placeholder="Buscar por nombre, correo, descripción..."
              leftIcon={<Search size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className={styles.filterSelect}>
            <Filter size={14} />
            <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="">Todas las categorías</option>
              {Object.keys(FILTER_CATEGORIES).map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className={styles.dateRange}>
            <Calendar size={14} />
            <input
              type="date"
              className={styles.dateInput}
              value={filterFechaDesde}
              onChange={(e) => setFilterFechaDesde(e.target.value)}
            />
            <span className={styles.dateSep}>—</span>
            <input
              type="date"
              className={styles.dateInput}
              value={filterFechaHasta}
              onChange={(e) => setFilterFechaHasta(e.target.value)}
            />
          </div>
        </div>

        {/* Active filter tipo pill */}
        {filterTipo && (
          <div className={styles.activeFilter}>
            <Badge variant={filterTipo === 'usuario' ? 'warning' : 'info'} size="sm">
              {filterTipo === 'usuario' ? 'Cambios de usuario' : 'Eventos del sistema'}
            </Badge>
            <button type="button" className={styles.clearFilter} onClick={() => setFilterTipo('')}>
              <X size={14} /> Limpiar filtro
            </button>
          </div>
        )}

        {/* Events list */}
        <div className={styles.tableCard}>
          {loading ? (
            <p className={styles.loading}>Cargando actividad...</p>
          ) : filtered.length === 0 ? (
            <p className={styles.empty}>No se encontraron eventos.</p>
          ) : (
            <div className={styles.eventList}>
              {filtered.map((log) => {
                const meta = getMeta(log.evento);
                const Icono = meta.icono;
                const ago = timeAgo(log.creadoEn);
                const displayUser = getDisplayUser(log, resolvers);

                // Pick user-friendly preview chips (skip hidden IDs)
                const previewEntries = log.detalles
                  ? Object.entries(log.detalles)
                      .filter(([k]) => !HIDDEN_ID_FIELDS.has(k))
                      .slice(0, 3)
                  : [];

                return (
                  <button
                    key={log.id}
                    type="button"
                    className={`${styles.eventRow} ${meta.tipo === 'usuario' ? styles.eventRowUsuario : ''}`}
                    onClick={() => handleRowClick(log)}
                  >
                    <div className={`${styles.eventIcon} ${styles[`eventIcon_${meta.variant}`]}`}>
                      <Icono size={16} />
                    </div>

                    <div className={styles.eventBody}>
                      <div className={styles.eventTop}>
                        <span className={styles.eventLabel}>{meta.label}</span>
                        {meta.tipo === 'usuario' && (
                          <Badge variant="warning" size="sm">Cambio de usuario</Badge>
                        )}
                      </div>
                      <div className={styles.eventDesc}>
                        <span className={styles.eventUser}>{displayUser}</span>
                        <span className={styles.eventSep}>·</span>
                        <span>{meta.descripcion}</span>
                      </div>
                      {previewEntries.length > 0 && (
                        <div className={styles.eventPreview}>
                          {previewEntries.map(([k, v]) => (
                            <span key={k} className={styles.previewChip}>
                              {formatDetailLabel(k)}: {formatDetailValue(k, v, resolvers)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className={styles.eventMeta}>
                      <span className={styles.eventDate}>{formatDate(log.creadoEn)}</span>
                      {ago && <span className={styles.eventAgo}>{ago}</span>}
                      <ChevronRight size={14} className={styles.eventChevron} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail modal */}
        <Modal
          open={!!selectedLog}
          onClose={() => setSelectedLog(null)}
          title="Detalle del evento"
          size="lg"
        >
          {selectedLog && <EventDetail log={selectedLog} resolvers={resolvers} />}
        </Modal>
      </div>
    </MainLayout>
  );
}

// ─── Event Detail View ──────────────────────────────────────

function EventDetail({ log, resolvers }: { log: RegistroAuditoria; resolvers: ResolverMap }) {
  const [showTechnical, setShowTechnical] = useState(false);
  const meta = getMeta(log.evento);
  const Icono = meta.icono;

  // Separate user-facing details from technical IDs
  const userDetails: [string, unknown][] = [];
  const technicalDetails: [string, string][] = [];

  // Resolve the main actor
  const actorName = getDisplayUser(log, resolvers);
  userDetails.push(['Responsable', actorName]);

  // Process detalles fields
  //
  // Un identificador crudo no le dice nada a quien audita. Cuando se puede
  // resolver a un nombre, el NOMBRE va a la sección principal y el id queda en
  // los detalles técnicos, que es donde sirve. Si no se puede resolver —cuenta
  // borrada, sala que ya no existe— el id se queda solo en lo técnico en vez de
  // ensuciar la vista con una cadena ilegible.
  const detalles = (log.detalles ?? {}) as Record<string, unknown>;
  if (log.detalles) {
    for (const [k, v] of Object.entries(detalles)) {
      // Los `<campo>Nombre` que escribe el backend se consumen como valor de su
      // campo base; mostrarlos aparte duplicaría la información.
      if (k.endsWith('Nombre') && k !== 'Nombre' && k.slice(0, -6) in detalles) continue;

      if (HIDDEN_ID_FIELDS.has(k)) {
        // 1º el nombre que guardó el backend, 2º el que resuelve esta pantalla.
        const guardado = detalles[`${k}Nombre`];
        let nombre = typeof guardado === 'string' && guardado ? guardado : null;
        if (!nombre && typeof v === 'string') {
          if (MEDICO_UID_FIELDS.has(k)) {
            const r = resolveUid(v, resolvers);
            if (r !== v) nombre = r;
          } else if (SALA_ID_FIELDS.has(k)) {
            const r = resolveSalaId(v, resolvers);
            if (r !== v) nombre = r;
          }
        }
        if (nombre) userDetails.push([k, nombre]);
        technicalDetails.push([k, nombre ? `${nombre} (${v})` : String(v)]);
      } else if (k !== 'email' && k !== 'uid' && k !== 'ip') {
        userDetails.push([k, v]);
      }
    }
  }

  // Add IP as a secondary detail (not hidden but at the end)
  if (log.ip) userDetails.push(['ip', log.ip]);

  // Browser info goes to technical section
  if (log.userAgent) technicalDetails.push(['userAgent', log.userAgent]);
  if (log.uid) technicalDetails.push(['uid', `${resolvers.medicos.get(log.uid) ?? 'Desconocido'} (${log.uid})`]);

  return (
    <div className={styles.detailView}>
      {/* Header */}
      <div className={styles.detailHeader}>
        <div className={`${styles.detailIconBig} ${styles[`eventIcon_${meta.variant}`]}`}>
          <Icono size={24} />
        </div>
        <div>
          <h3 className={styles.detailTitle}>{meta.label}</h3>
          <p className={styles.detailDesc}>{meta.descripcion}</p>
          <div className={styles.detailBadges}>
            <Badge variant={meta.variant} size="sm">
              {meta.variant === 'success' ? 'Exitoso' : meta.variant === 'error' ? 'Error / Rechazado' : meta.variant === 'warning' ? 'Acción administrativa' : meta.variant === 'info' ? 'Informativo' : 'Normal'}
            </Badge>
            <Badge variant={meta.tipo === 'usuario' ? 'warning' : 'neutral'} size="sm">
              {meta.tipo === 'usuario' ? 'Cambio de usuario' : 'Evento del sistema'}
            </Badge>
          </div>
        </div>
      </div>

      {/* Timestamp */}
      <div className={styles.detailTimestamp}>
        <Clock size={14} />
        <span>{formatDateLong(log.creadoEn)}</span>
      </div>

      {/* User-facing details */}
      {userDetails.length > 0 && (
        <div className={styles.detailSection}>
          <h4 className={styles.detailSectionTitle}>Información del evento</h4>
          <div className={styles.detailGrid}>
            {userDetails.map(([key, value]) => {
              // The first entry "Responsable" uses the key as-is
              const label = key === 'Responsable' ? key : formatDetailLabel(key);
              // Los campos de identificador llegan ya resueltos a nombre desde
              // el bucle de arriba; volver a pasarlos por el resolver los
              // convertiría de nuevo en id.
              const yaResuelto = key === 'Responsable' || HIDDEN_ID_FIELDS.has(key);
              const display = yaResuelto ? String(value) : formatDetailValue(key, value, resolvers);
              return (
                <div key={key} className={styles.detailField}>
                  <span className={styles.detailFieldLabel}>{label}</span>
                  <span className={styles.detailFieldValue}>{display}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Technical details (collapsible) */}
      {technicalDetails.length > 0 && (
        <div className={styles.detailSection}>
          <button
            type="button"
            className={styles.techToggle}
            onClick={() => setShowTechnical(!showTechnical)}
          >
            {showTechnical ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            <span>Detalles técnicos</span>
          </button>
          {showTechnical && (
            <div className={styles.techGrid}>
              {technicalDetails.map(([key, value]) => (
                <div key={key} className={styles.techField}>
                  <span className={styles.techFieldLabel}>{key}</span>
                  <span className={styles.techFieldValue}>{value}</span>
                </div>
              ))}
              <div className={styles.techField}>
                <span className={styles.techFieldLabel}>evento</span>
                <span className={styles.techFieldValue}>{log.evento}</span>
              </div>
              <div className={styles.techField}>
                <span className={styles.techFieldLabel}>registro_id</span>
                <span className={styles.techFieldValue}>{log.id}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
